import React, { useId } from 'react';
import styled from 'styled-components';
import {
  DateField,
  DateRangeField,
  Field,
  formatCalendarDate,
  Icon,
  Input,
  Note,
  Switch,
} from '@repo/design-system';
import { semantic, space, typeRole } from '@repo/design-tokens';

/**
 * The fields *New Standard Hours From a Date* and *Edit details* share (spec
 * opening-hours-edit-standard-details, R3.2 and R4.1; the review page's decisions 1 and 3), so adding
 * and editing a set stay alike: **Name**; **Starts on** — a date field, or for a running set the
 * date as text, since a start already passed stays; the **No end date** switch; and **Ends on** while
 * it is off. A start that can move and an end both shown use one `DateRangeField` (*Dates*), so the
 * popover shows one calendar. Under them, one `peri` note: what else the change does, wired to the
 * date fields so it is read with them.
 *
 * Controlled; the host owns the values, the limits and the note's sentences.
 */

export interface StandardHoursFieldsProps {
  /** Prefix for the fields' ids, one per popover. */
  idPrefix: string;
  name: string;
  onName: (name: string) => void;
  /** Shown under **Name**, and holds the host's action (R3.9). */
  nameProblem: string | null;
  /** A running set's start, shown as text; null when the start is a field. */
  fixedStart: string | null;
  from: string | null;
  to: string | null;
  noEnd: boolean;
  onDates: (dates: { from: string | null; to: string | null; noEnd: boolean }) => void;
  start: { min: string; max: string };
  end: { min: string; max: string };
  /** Why *No end date* cannot be on, as the switch's description; null when it can. */
  noEndReason: string | null;
  today: string;
  disabled: boolean;
  /** The note's sentences; no note when empty. */
  note: readonly string[];
  className?: string;
}

const StandardHoursFields: React.FC<StandardHoursFieldsProps> = ({
  idPrefix,
  name,
  onName,
  nameProblem,
  fixedStart,
  from,
  to,
  noEnd,
  onDates,
  start,
  end,
  noEndReason,
  today,
  disabled,
  note,
  className,
}) => {
  const noteId = useId();
  const describedBy = note.length > 0 ? noteId : undefined;
  const range = fixedStart === null && !noEnd;

  return (
    <div className={className}>
      <Field label="Name" error={nameProblem ?? undefined}>
        {w => (
          <Input {...w} value={name} disabled={disabled} onChange={e => onName(e.target.value)} />
        )}
      </Field>

      {fixedStart !== null && (
        <div className="fixed-start">
          <span className="label">Starts on</span>
          <span className="value">{formatCalendarDate(fixedStart)} · started, so it stays</span>
        </div>
      )}
      {fixedStart === null && noEnd && (
        <DateField
          id={`${idPrefix}-from`}
          label="Starts on"
          value={from}
          onChange={value => onDates({ from: value, to, noEnd })}
          min={start.min}
          max={start.max}
          today={today}
          disabled={disabled}
          aria-describedby={describedBy}
        />
      )}

      <Switch
        surface="on-glass"
        checked={noEnd}
        disabled={disabled || (!noEnd && noEndReason !== null)}
        description={!noEnd && noEndReason ? noEndReason : undefined}
        onChange={e =>
          onDates({ from, to: e.currentTarget.checked ? null : to, noEnd: e.currentTarget.checked })
        }
      >
        No end date
      </Switch>

      {range && (
        <DateRangeField
          id={`${idPrefix}-dates`}
          label="Dates"
          value={{ from, to }}
          onChange={value => onDates({ from: value.from, to: value.to, noEnd })}
          min={start.min}
          max={end.max}
          today={today}
          disabled={disabled}
          aria-describedby={describedBy}
        />
      )}
      {fixedStart !== null && !noEnd && (
        <DateField
          id={`${idPrefix}-to`}
          label="Ends on"
          value={to}
          onChange={value => onDates({ from, to: value, noEnd })}
          min={end.min}
          max={end.max}
          today={today}
          disabled={disabled}
          aria-describedby={describedBy}
        />
      )}

      {note.length > 0 && (
        <Note tone="peri" icon={<Icon name="circle-info" size="xs" />}>
          <span id={noteId}>{note.join(' ')}</span>
        </Note>
      )}
    </div>
  );
};

export default styled(StandardHoursFields)`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};

  .fixed-start {
    display: flex;
    flex-direction: column;
    gap: ${space[1]};
  }

  .fixed-start .label {
    ${typeRole.caption1Strong};
    color: ${semantic.text.primary};
  }

  .fixed-start .value {
    ${typeRole.body1};
    color: ${semantic.text.secondary};
  }
`;
